/* ─────────────────────────────────────────────
   PRICE LIST - one source of truth

   Every price on the site comes from here: the /pricing table, the price
   tables on the individual hair-service pages, and the AggregateOffer ranges
   in the Service JSON-LD. Change a number once and all three follow.

   Prices are always rendered as TEXT in HTML tables, never as an image -
   a competitor publishes their price list as a picture, which Google cannot
   read, and every "cena" search that produces is ours to take.

   Amounts are RSD strings, not numbers, because a price may be a single
   amount ("3000"), a range ("3000-3500") or an either/or pair ("700 / 1000").

   .mjs on purpose: src/seo/head.mjs imports it to build the offer ranges, and
   scripts/prerender.mjs runs that in plain Node with no bundler.
   ───────────────────────────────────────────── */

/* ── Hair services ────────────────────────────
   Named because five pages and the Service schema reference them directly.
   The /pricing table below reuses these same arrays, so the price list and
   the service pages can never drift apart. */
export const HAIR = {
  /** Colouring with the client's own dye, and root touch-ups. */
  farbanjeUsluzno: '1100-1400',
  farbanjeIzrastak: '3000',
  /** Full colour + blow-dry, by length: short, medium, long, extra long. */
  farbanjeCela: ['3000', '3500', '4000', '4500-6000'],
  /** Toner + blow-dry, same four lengths. */
  preliv: ['2700-3000', '3000-3500', '3500-4500', '4500-6000'],
  /** Root bleach + toner + blow-dry, same four lengths. */
  blajhanjeIzrastka: ['4000', '4500', '5000', '5500-7000'],
  /** Full bleach + toner + blow-dry: short, medium, long & extra long. */
  blajhanjeCele: ['4000-7000', '7000-10000', '10000-14000'],
  /** Balayage / air touch / highlights, four lengths. */
  balayage: ['8000-10000', '10000-13000', '13000-16000', '16000-18000'],
  /** Treatments: stem cells, Olaplex, hair botox, Quick Repair. */
  tretmani: ['3000', '2500', '1700', '500'],
  /** Men's: classic cut, fade, wash. */
  musko: ['700', '900', '200'],
};

/* ── /pricing table ───────────────────────────
   Indexed [section][group][item], matching the localized labels under
   `pricing.sections` in the locale files exactly. Adding a row here means
   adding the matching label to all three locales. */
export const PRICE_TABLE = [
  // Frizer / Hairdresser
  [
    ['600', '800', '900', '1100'],
    ['1300', '1400', '1500', '1600'],
    [HAIR.farbanjeUsluzno, HAIR.farbanjeIzrastak],
    HAIR.farbanjeCela,
    HAIR.preliv,
    HAIR.blajhanjeIzrastka,
    HAIR.blajhanjeCele,
    HAIR.balayage,
    ['400', '1000', '1300'],
    ['3000', '3500', '4000', '5000'],
    ['1800', '2200', '2400', '2800'],
    ['2300', '2500', '2800', '3000'],
    ['3000-5000', '700'],
    HAIR.tretmani,
    HAIR.musko,
  ],
  // Trajna šminka / Permanent makeup
  [
    ['18000', '5000', '8000', '10000', '12000', '15000'],
    ['16000', '3000', '6000', '8000', '10000', '13000'],
  ],
  // Svilene trepavice / Silk eyelash extensions
  [
    ['2600', '2800', '3000', '3100', '3000'],
    ['2300', '2500', '2800', '2900', '2800', '600-900'],
  ],
  // Lifting trepavica / Lash lift
  [
    ['2500', '2000', '3200'],
  ],
  // Profesionalna šminka / Professional makeup
  [
    ['3400', '5000', '500', '300'],
  ],
  // Nokti / Nails
  [
    ['1200', '1600', '900', '900', '1100', '2400', '2300', '700'],
    ['2600', '2800', '3000', '3200'],
    ['2300', '2500', '2700', '3000'],
    ['400'],
    ['50', '100-150', '100', '50'],
  ],
  // Pedikir / Pedicure
  [
    ['1800', '2000', '2300', '1800', '700'],
  ],
  // Depilacija / Waxing
  [
    ['600', '250', '150', '500', '500', '1500', '500', '700 / 1000', '450 / 900', '450 / 900'],
    ['900 / 1300', '600', '900', '1200', '700'],
    ['1600', '1600', '2300', '2700', '3800'],
  ],
];

/* ── display helpers ──────────────────────────── */

/**
 * RSD → EUR, keeping the shape of the string.
 *
 * A price may be a plain amount ("2600"), a range ("3000-3500") or an
 * either/or pair ("700 / 1000"), so every number is converted while the
 * separators around it are left alone. Shared by the /pricing table and the
 * price lists on the individual service pages, so both convert identically.
 */
export const toEur = (priceRsd, rate) =>
  String(priceRsd).replace(/\d+/g, (amount) => (Number(amount) / rate).toFixed(1));

/** Every number that appears anywhere in a price value or list of values. */
function amountsIn(value) {
  const values = Array.isArray(value) ? value : [value];
  return values.flatMap((v) => (String(v).match(/\d+/g) || []).map(Number));
}

/**
 * Lowest and highest amount across the given price entries - feeds
 * AggregateOffer.lowPrice / highPrice so the schema can never contradict the
 * table printed on the same page.
 */
export function priceRange(...entries) {
  const amounts = entries.flatMap(amountsIn);
  return { low: String(Math.min(...amounts)), high: String(Math.max(...amounts)) };
}

/** AggregateOffer range per service page, derived from the table above. */
export const SERVICE_PRICE_RANGE = {
  '/balayage': priceRange(HAIR.balayage),
  '/farbanje-kose': priceRange(
    HAIR.farbanjeUsluzno,
    HAIR.farbanjeIzrastak,
    HAIR.farbanjeCela,
    HAIR.preliv,
    HAIR.blajhanjeIzrastka,
    HAIR.blajhanjeCele,
  ),
  '/tretmani-kose': priceRange(HAIR.tretmani),
  '/musko-sisanje': priceRange(HAIR.musko),
};

/* ── Extensions and wigs ──────────────────────
   Stock is only partly in, so no prices are published yet and the pages
   deliberately show none.

   While this is false the Product JSON-LD is not emitted at all. That is on
   purpose: Google needs `offers`, `review` or `aggregateRating` before a
   Product is eligible for a rich result, and a Product with none of them is
   flagged by the Rich Results Test. A price of 0 or an empty string is worse
   still - that is invalid.

   When the stock is complete: fill in the ranges below and flip this to true.
   The Product block, with its AggregateOffer, then appears on both pages
   without any change to the components. */
export const SHOW_PRODUCT_PRICES = false;

export const PRODUCT_PRICE_RANGE = {
  // '/ekstenzije-za-kosu': { low: '0000', high: '0000' },
  // '/perike': { low: '0000', high: '0000' },
};
